import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  businessQuery,
  datamodelItemDeleteMutation,
  datamodelItemMutation,
  datamodelItemQuery,
  datamodelQuery,
  datamodelsQuery,
} from '@queries';
import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import { ChevronRightIcon } from '@svgs';

import { ContentDatabaseItemForm } from '../components/ContentDatabaseItemForm/ContentDatabaseItemForm.jsx';
import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

export const ContentDatabaseItem = () => {
  const { t, i18n } = useTranslation();
  const { businessID, datamodelItemID } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // get business
  const { data: businessData } = useQuery(businessQuery(businessID));

  // get datamodel item
  const { data: datamodelItemData, isPending: datamodelItemIsPending } =
    useQuery(
      datamodelItemQuery(datamodelItemID, {
        staleTime: Infinity,
      }),
    );

  // get datamodels
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      datamodel_type: 'datamodel_item',
    }),
  );

  // get datamodel id associated with datamodel item
  const datamodelId =
    datamodelsData && datamodelItemData
      ? datamodelsData.objects.find(
          (dm) => dm.id == datamodelItemData.datamodel_id,
        ).id
      : null;

  // get datamodel detail for the type
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelId),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  // save datamodel
  const mutation = useMutation(
    datamodelItemMutation(datamodelItemID, {
      onSuccess: () => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['datamodels/items', 'detail', datamodelItemID],
        });
      },
    }),
  );

  // delete datamodel
  const deleteMutation = useMutation(
    datamodelItemDeleteMutation(datamodelItemID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({
          queryKey: ['datamodels/items', 'list'],
        });
        // navigate to the list view
        navigate(`/dashboard/projects/${businessID}/contentitems`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <Link to={`/dashboard/projects/${businessID}/contentitems`}>
                Content database
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {datamodelItemIsPending && <span>...</span>}
              {!datamodelItemIsPending && <span>{datamodelItemData.name}</span>}
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-manage-content">
            {(datamodelItemIsPending ||
              datamodelsIsPending ||
              datamodelIsPending ||
              !allFinished) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {!datamodelItemIsPending &&
              !datamodelsIsPending &&
              !datamodelIsPending &&
              allFinished && (
                <>
                  <h1>{datamodelItemData.name}</h1>
                  <ContentDatabaseItemForm
                    datamodelItemData={datamodelItemData}
                    disabled={mutation.isPending || deleteMutation.isPending}
                    mutation={mutation}
                    datamodelData={datamodelData}
                    relatedDatamodels={relatedDatamodels}
                  />
                  <div className="sb-message-action-section">
                    <ActionObject
                      onAction={() => deleteMutation.mutate()}
                      loading={deleteMutation.isPending}
                      actionName="Delete"
                      actionVerb="Deleting"
                      label="Delete content item"
                      note="This will be permanently deleted"
                    />
                  </div>
                </>
              )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
